import React, { useRef, useState, useMemo } from 'react';
import { VStack, Flex, Box, Button } from '@chakra-ui/react';
import { KText } from '@/ui-library/components/text';
import { KTag } from '@/ui-library/components';
import { FormBuilder } from '@/components/formBuilder';
import { SelectOptionWithAvatar } from '@/components/formBuilder/SelectComponents';
import { KMenuButton } from '@/ui-library/components/atoms/KMenuButton/KMenuButton';

interface CurrentUser {
  id?: number;
  first_name: string;
  last_name: string;
  avatar_url?: string;
}

interface ReassignPopoverProps {
  onReassign: (
    _userId: number,
    _requisitionId?: number,
    _applyToGroup?: boolean
  ) => void;
  title: string;
  description: string;
  fieldLabel: string;
  currentUser?: CurrentUser;
  isLoading?: boolean;
  triggerElement?: React.ReactNode;
  ctaLabel?: string;
  isPartOfGroup?: boolean;
  groupRequisitions?: any[];
  currentRequisitionId?: number;
}

export const ReassignPopover: React.FC<ReassignPopoverProps> = ({
  onReassign,
  title,
  description,
  fieldLabel,
  currentUser,
  isLoading = false,
  triggerElement,
  ctaLabel = 'Re-assign',
  isPartOfGroup = false,
  groupRequisitions = [],
  currentRequisitionId,
}) => {
  const formRef = useRef<any>(null);
  const [selectedRequisitionId, setSelectedRequisitionId] = useState<
    number | undefined
  >(currentRequisitionId);
  const [showForm, setShowForm] = useState(false);
  const [applyToGroup, setApplyToGroup] = useState(false);
  const [hasUserChanged, setHasUserChanged] = useState(false);
  const [menuKey, setMenuKey] = useState(0);

  const formConfig = [
    {
      name: 'user',
      label: '',
      type: 'autoComplete',
      isRequired: true,
      validationType: 'object',
      errorMessage: `${fieldLabel} is required`,
      containerStyles: {},
      fetchConfig: {
        method: 'get',
        endPoint: `account/users/search?filter_by[status][]=active&filter_by[permission][]=collaborate_on_requisitions&filter_value=`,
        processResponse: (res: any) =>
          res?.data?.users
            ?.filter((item: any) => item?.id !== currentUser?.id)
            ?.map((item: any) => {
              const userOption = {
                name: `${item?.first_name} ${item?.last_name}`,
                id: item?.id,
                avatar: item?.avatar_thumb_url,
                ...item,
              };
              return { ...userOption, value: userOption };
            }),
        httpType: 'v1',
      },
      showAvatar: true,
      customFormatLabel: SelectOptionWithAvatar,
      updateEndpointOnChange: true,
      useInput: true,
      valueKey: 'value',
      labelKey: 'name',
      placeholder: fieldLabel,
    },
  ];

  const initialValues = {
    user:
      currentUser && currentUser.id !== undefined && currentUser.id !== null
        ? {
            id: currentUser.id,
            name: `${currentUser.first_name ?? ''} ${currentUser.last_name ?? ''}`.trim(),
            avatar: currentUser.avatar_url,
          }
        : null,
  };

  const handleMenuItemClick = (isApplyToGroup: boolean) => {
    setApplyToGroup(isApplyToGroup);
    setSelectedRequisitionId(isApplyToGroup ? undefined : currentRequisitionId);
    setHasUserChanged(false);
    setShowForm(true);
  };

  const handleFormSubmit = (values: any) => {
    onReassign(values.user?.id, selectedRequisitionId, applyToGroup);
    // Force menu to close by changing key
    setMenuKey((prev) => prev + 1);
  };

  // Get group requisition codes for display
  const groupRequisitionCodes = useMemo(() => {
    if (groupRequisitions.length > 0) {
      return groupRequisitions
        .map((req) => req.code)
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    }
    return [];
  }, [groupRequisitions]);

  // Menu items for group requisitions (similar to Edit menu pattern)
  const reassignMenuItems = useMemo(() => {
    return [
      {
        id: 'reassign_single',
        label: `${ctaLabel} this requisition alone`,
        description: `${ctaLabel}ing this requisition alone will detach it from the group and assign a new requisition ID to it`,
        applyToGroup: false,
      },
      {
        id: 'reassign_all',
        label: `${ctaLabel} this and all related requisitions`,
        description: '',
        applyToGroup: true,
        groupRequisitionCodes: groupRequisitionCodes,
      },
    ];
  }, [groupRequisitionCodes, ctaLabel]);

  // Menu content with form
  const menuContentWithForm = (
    <VStack align="stretch" spacing={4} p={4} minW="400px">
      {/* Header */}
      <VStack align="stretch" spacing={1}>
        <KText fontSize="md" fontWeight="semibold" color="secondary-900">
          {title}
        </KText>
        <KText fontSize="sm" color="secondary-600">
          {description}
        </KText>
      </VStack>

      {/* Form */}
      <FormBuilder
        formRef={formRef}
        config={formConfig}
        initialValues={initialValues}
        onSubmit={handleFormSubmit}
        formContainerProps={{ marginTop: '0px !important' }}
        isObserveEnabled={true}
        observFunc={(values: any) => {
          const hasChanged = values.user?.id !== initialValues.user?.id;
          setHasUserChanged(hasChanged);
        }}
      />

      {/* Submit Button */}
      <Flex gap="10px" justifyContent="flex-end" mt="0px !important">
        <Button
          variant="secondary"
          onClick={() => {
            if (isPartOfGroup && groupRequisitions.length > 0 && showForm) {
              // In group mode, go back to menu items
              setShowForm(false);
              setSelectedRequisitionId(currentRequisitionId);
              setApplyToGroup(false);
              setHasUserChanged(false);
            } else {
              // Close the menu by changing key
              setMenuKey((prev) => prev + 1);
            }
          }}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={() => {
            if (formRef.current) {
              formRef.current.handleSubmit();
            }
          }}
          isLoading={isLoading}
          isDisabled={!hasUserChanged}
        >
          {ctaLabel}
        </Button>
      </Flex>
    </VStack>
  );

  // Menu content for group mode - shows options first, then form when option is selected
  const groupMenuContent = showForm ? (
    <Box>{menuContentWithForm}</Box>
  ) : (
    <>
      {reassignMenuItems.map((item, index) => (
        <Box
          key={item.id}
          p="12px"
          borderTop={index > 0 ? '1px solid' : undefined}
          borderColor={index > 0 ? 'border-color' : undefined}
          cursor="pointer"
          onClick={() => handleMenuItemClick(item.applyToGroup)}
          _hover={{ bg: 'secondary-50' }}
          role="group"
          className="group"
        >
          <Flex direction="column" gap="6px">
            <Flex direction="column" gap="3px">
              <KText size="sm-medium" color="secondary-900">
                {item.label}
              </KText>
              {item.description && (
                <KText size="sm-regular" color="secondary-700">
                  {item.description}
                </KText>
              )}
            </Flex>
            {item.applyToGroup &&
              item.groupRequisitionCodes &&
              item.groupRequisitionCodes.length > 0 && (
                <Flex flexWrap="wrap" gap="6px" maxH="200px" overflowY="auto">
                  {item.groupRequisitionCodes.map((code: string) => (
                    <KTag
                      key={code}
                      value={code}
                      color="secondary-700"
                      _groupHover={{
                        bgColor: 'white',
                        borderColor: 'border-color',
                        color: 'secondary-700',
                        borderWidth: '1px',
                      }}
                    />
                  ))}
                </Flex>
              )}
          </Flex>
        </Box>
      ))}
    </>
  );

  // When part of group, show menu items + form; otherwise show form directly
  const menuContent =
    isPartOfGroup && groupRequisitions.length > 0
      ? groupMenuContent
      : menuContentWithForm;

  return (
    <Box onClick={(e) => e.stopPropagation()}>
      <KMenuButton
        key={menuKey}
        variant="tertiary"
        triggerContent={
          triggerElement || (
            <Box>
              <KText color="primary-600" size="sm-medium">
                Re-assign
              </KText>
            </Box>
          )
        }
        menuContentProps={{
          minW: '400px',
          maxW: '320px',
          boxShadow: 'drop-shadows-bottom',
          zIndex: 1500,
          borderRadius: '6px',
          padding: '0px !important',
          onClick: (e: any) => {
            e.stopPropagation();
            e.preventDefault();
          },
        }}
        p="6px !important"
        _hover={{ bg: 'primary-100' }}
        _active={{ bg: 'primary-100' }}
        iconProps={{ display: isPartOfGroup ? 'inline' : 'none' }}
        dividerProps={{ display: 'none' }}
        menuContent={menuContent}
        isPortal={true}
        onClose={() => {
          // Reset all state when menu closes
          setShowForm(false);
          setSelectedRequisitionId(currentRequisitionId);
          setApplyToGroup(false);
          setHasUserChanged(false);
        }}
      />
    </Box>
  );
};
